import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Clipboard, Folder, Clock, CalendarClock, AlertCircle, User, CheckCircle2,
  MoreHorizontal, ArrowRight, Loader2, ChevronRight, ChevronDown, MessageSquare, CircleDot, Bell, Building2, AlertTriangle,
  Flame, Wrench, Disc3, FileText, Factory, X, Target,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  "Ikke startet": { dot: "bg-stone-300", text: "text-stone-500" },
  "I gang": { dot: "bg-[#A08979]", text: "text-[#806B61]" },
  "Afventer": { dot: "bg-[#C9A83F]", text: "text-[#8A6E17]" },
  "Færdig": { dot: "bg-[#7E856C]", text: "text-[#7E856C]" },
};
const PROJ_STATUS = { ikke_igangsat: "Ikke igangsat", aktiv: "Aktiv", paused: "Pauset", afsluttet: "Afsluttet" };
const PROJ_DOT = { ikke_igangsat: "bg-stone-300", aktiv: "bg-[#7E856C]", paused: "bg-[#A08979]", afsluttet: "bg-stone-300" };
const CASE_META = { "Ny": { text: "text-stone-500", dot: "bg-stone-300" }, "I gang": { text: "text-[#806B61]", dot: "bg-[#A08979]" }, "Afventer svar": { text: "text-[#8A6E17]", dot: "bg-[#C9A83F]" }, "Færdig": { text: "text-[#7E856C]", dot: "bg-[#7E856C]" } };
const LOAD_META = {
  "Ingen belastning": { dot: "bg-stone-300", bar: "bg-stone-300", chip: "bg-stone-50 text-stone-500 border-stone-200" },
  "Normal": { dot: "bg-[#7E856C]", bar: "bg-[#7E856C]", chip: "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]" },
  "Høj belastning": { dot: "bg-[#A08979]", bar: "bg-[#A08979]", chip: "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]" },
  "Overbelastet": { dot: "bg-[#A83232]", bar: "bg-[#A83232]", chip: "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" },
};

function greeting() {
  const h = new Date().getHours();
  if (h < 10) return "Godmorgen";
  if (h < 18) return "God eftermiddag";
  return "God aften";
}
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const deadlineText = (t) => {
  if (!t.deadline) return "Ingen deadline";
  if (t.is_delayed) return `${t.delay_days} dage forsinket`;
  if (t.days_until === 0) return "I dag";
  if (t.days_until === 1) return "I morgen";
  if (t.days_until > 0) return `Om ${t.days_until} dage`;
  return daDate(t.deadline);
};

function TaskCard({ item, busy, onDone, nav }) {
  const sm = STATUS_META[item.status] || { dot: "bg-[#A08979]", text: "text-[#806B61]" };
  const ready = item.is_first || item.prev_done;
  const busyKey = item.kind === "case" ? item.task_id : item.step_id;
  return (
    <div className={`rounded-2xl border p-6 flex flex-col ${item.is_delayed ? "border-[#A83232]" : "border-[#EBE8E3]"} bg-white`} data-testid={`mytask-${item.testid}`}>
      <div className="flex items-start justify-between gap-2">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${item.is_delayed ? "text-[#A83232]" : sm.text}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${item.is_delayed ? "bg-[#A83232]" : sm.dot}`} /> {item.is_delayed ? "Forsinket" : item.status}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-stone-300 hover:text-stone-600 -mr-1 -mt-1 p-1" data-testid={`mytask-menu-${item.testid}`}><MoreHorizontal className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-xl">
            {item.can_complete && (
              <DropdownMenuItem data-testid={`mytask-done-${item.testid}`} disabled={busy === busyKey} onClick={() => onDone(item)}>
                <CheckCircle2 className="h-4 w-4 mr-2" /> Marker som færdig
              </DropdownMenuItem>
            )}
            <DropdownMenuItem data-testid={`mytask-docs-${item.testid}`} onClick={() => nav(item.nav_to)}>
              <Clipboard className="h-4 w-4 mr-2" /> Vis dokumenter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className="font-semibold text-[#1A1919] mt-3 leading-snug">{item.title}</p>
      <p className="text-sm text-stone-500 mt-0.5">{item.subtitle}</p>
      <div className="mt-4">
        <p className="label-caps">{item.kind === "finding" ? "Auditfund" : item.kind === "le_action" ? "LE-handling" : item.kind === "goal" ? "Kvalitets-/miljømål" : "Din opgave"}</p>
        <p className="text-[15px] font-medium text-[#1A1919] mt-1">{item.task_name}</p>
      </div>
      {item.kind !== "finding" && item.kind !== "le_action" && item.kind !== "goal" && (
      <div className={`mt-4 flex items-start gap-2.5 rounded-xl border px-3 py-2.5 ${ready ? "border-[#D3D8C6] bg-[#E7EADF]/50" : "border-[#E6DDD6] bg-[#F4EFEC]/50"}`} data-testid={`mytask-ready-${item.testid}`}>
        {ready ? <CheckCircle2 className="h-4 w-4 text-[#7E856C] mt-0.5 shrink-0" strokeWidth={2} /> : <Clock className="h-4 w-4 text-[#A08979] mt-0.5 shrink-0" strokeWidth={2} />}
        <div className="min-w-0">
          <p className={`text-xs font-semibold ${ready ? "text-[#7E856C]" : "text-[#806B61]"}`}>{ready ? "Klar til at gå i gang" : "Afventer forrige trin"}</p>
          <p className="text-xs text-stone-500 mt-0.5 truncate">{item.is_first ? item.first_label : ready ? "Forrige trin er meldt færdigt" : `${item.prev_step_name} · ${item.prev_step_status}`}</p>
        </div>
      </div>
      )}
      <div className="mt-4 mb-6 space-y-2 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="text-stone-400">Deadline</span>
          <span className={`font-medium ${item.is_delayed ? "text-[#A83232]" : "text-stone-700"}`}>{deadlineText(item)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-stone-400">{item.responsible_label}</span>
          <span className="text-stone-500">{item.responsible_name || "—"}</span>
        </div>
        {item.kind === "case" && (
          <div className="flex items-center justify-between gap-2">
            <span className="text-stone-400">Kilde</span>
            <span className="text-stone-500 text-right truncate max-w-[60%]">{item.source}</span>
          </div>
        )}
      </div>
      {(() => {
        const all = item.milestones || [];
        if (all.length === 0) return null;
        const doneN = all.filter((m) => m.done).length;
        const pending = all.filter((m) => !m.done);
        return (
          <div className="-mt-2 mb-6 pt-4 border-t border-[#F3F1EC]" data-testid={`mytask-milestones-${item.testid}`}>
            <p className="label-caps mb-2">Delfrister · {doneN}/{all.length} udført</p>
            {pending.length === 0 ? (
              <p className="text-xs text-stone-400">Alle delfrister udført</p>
            ) : (
              <div className="space-y-1.5">
                {pending.map((m, i) => (
                  <div key={m.id || i} className="flex items-center justify-between gap-2 text-xs" data-testid={`mytask-milestone-${item.testid}-${i}`}>
                    <span className="inline-flex items-center gap-1.5 min-w-0"><CalendarClock className="h-3 w-3 text-[#A08979] shrink-0" /><span className="truncate text-stone-600">{m.label || "Delmål"}</span></span>
                    <span className="text-stone-500 tabular-nums shrink-0">{m.deadline ? daDate(m.deadline) : "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
      <Button className="rounded-xl w-full justify-center" style={{ marginTop: "auto" }} data-testid={`mytask-open-${item.testid}`} onClick={() => nav(item.nav_to)}>{item.button_label}</Button>
    </div>
  );
}


function CreatedCaseCard({ c, nav }) {
  const TYPE = { kvalitet: "Kvalitet", indkoeb: "Indkøb" };
  const meta = CASE_META[c.status] || CASE_META["Ny"];
  const pct = c.tasks_total ? Math.round(c.tasks_done / c.tasks_total * 100) : 0;
  return (
    <div className="rounded-2xl border border-[#EBE8E3] bg-white p-6 flex flex-col" data-testid={`created-case-${c.case_id}`}>
      <div className="flex items-start justify-between gap-2">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${c.is_delayed ? "text-[#A83232]" : meta.text}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${c.is_delayed ? "bg-[#A83232]" : meta.dot}`} /> {c.is_delayed ? "Forsinket" : c.status}
        </span>
        <span className="text-xs text-stone-400">{TYPE[c.type] || c.type}</span>
      </div>
      <p className="font-semibold text-[#1A1919] mt-3 leading-snug">{c.title}</p>
      <p className="text-sm text-stone-500 mt-0.5">{c.source_project_name || c.customer_name || "—"}</p>
      <div className="mt-4">
        <p className="label-caps">Aktuel opgave</p>
        <p className="text-[15px] font-medium text-[#1A1919] mt-1">{c.current_task_name || "Alle opgaver færdige"}</p>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2 text-xs"><span className="text-stone-400">Fremgang</span><span className="font-medium text-stone-500">{c.tasks_done}/{c.tasks_total}</span></div>
        <div className="h-1 rounded-full bg-[#ECEAE5] overflow-hidden"><div className="h-full rounded-full bg-[#A83232]" style={{ width: `${pct}%` }} /></div>
      </div>
      <div className="mt-4 mb-6 space-y-2 text-sm">
        <div className="flex items-center justify-between gap-2"><span className="text-stone-400">Deadline</span><span className={`font-medium ${c.is_delayed ? "text-[#A83232]" : "text-stone-700"}`}>{c.deadline ? daDate(c.deadline) : "Ingen frist"}</span></div>
        <div className="flex items-center justify-between gap-2"><span className="text-stone-400">Sagsansvarlig</span><span className="text-stone-500">{c.responsible_name || "—"}</span></div>
      </div>
      <Button className="rounded-xl w-full justify-center" style={{ marginTop: "auto" }} data-testid={`created-case-open-${c.case_id}`} onClick={() => nav(`/sag/${c.case_id}`)}>Åbn sag</Button>
    </div>
  );
}


export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const deps = user?.departments || [];
  const prodOnly = user?.role !== "admin" && deps.includes("produktion") && !["salg", "indkoeb", "kvalitet"].some((x) => deps.includes(x));
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [bk, setBk] = useState(null);
  useEffect(() => { if (user?.role === "admin") api.get("/backup/status").then((r) => setBk(r.data)).catch(() => {}); }, [user]);
  const [openLoad, setOpenLoad] = useState(null);
  const [expandedCases, setExpandedCases] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const [statusAlerts, setStatusAlerts] = useState(null);
  const [caseAlerts, setCaseAlerts] = useState(null);
  const [docAlerts, setDocAlerts] = useState(null);
  const [myFindings, setMyFindings] = useState([]);
  const [myLeActions, setMyLeActions] = useState([]);
  const [myGoals, setMyGoals] = useState([]);

  const load = useCallback(async () => {
    if (prodOnly) { setLoading(false); return; }
    try {
      const r = await api.get("/dashboard"); setD(r.data);
      try { const a = await api.get("/me/message-alerts"); setAlerts(a.data); } catch (_) { /* noop */ }
      try { const sa = await api.get("/me/status-alerts"); setStatusAlerts(sa.data); } catch (_) { /* noop */ }
      try { const ca = await api.get("/me/case-alerts"); setCaseAlerts(ca.data); } catch (_) { /* noop */ }
      try { const dca = await api.get("/me/document-alerts"); setDocAlerts(dca.data); } catch (_) { /* noop */ }
      try { const mf = await api.get("/audit/my-open-findings"); setMyFindings(mf.data || []); } catch (_) { /* noop */ }
      try { const ml = await api.get("/le/my-open-actions"); setMyLeActions(ml.data || []); } catch (_) { /* noop */ }
      try { const mg = await api.get("/le/my-open-goals"); setMyGoals(mg.data || []); } catch (_) { /* noop */ }
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke hente dashboard"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markDone = async (item) => {
    const key = item.kind === "case" ? item.task_id : item.step_id;
    setBusy(key);
    try {
      if (item.kind === "case") await api.patch(`/cases/${item.case_id}/tasks/${item.task_id}`, { status: "Færdig" });
      else await api.patch(`/project-steps/${item.step_id}`, { status: "Færdig" });
      toast.success("Markeret som færdig"); await load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke opdatere"); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-32 text-stone-400" data-testid="home-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Henter dit dashboard…</div>;
  if (prodOnly) return <ProductionHome user={user} nav={nav} />;
  if (!d) return null;
  const todayISO = new Date().toISOString().slice(0, 10);

  const { name, kpis, focus, priorities, my_projects, company } = d;
  const caseTasks = d.my_case_tasks || [];
  const createdCases = d.my_created_cases || [];
  const isSalgView = user?.role === "admin" || (user?.departments || []).includes("salg");
  const initials = (user?.name || name || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const TYPE_LABEL = { kvalitet: "Kvalitet", indkoeb: "Indkøb" };
  const myTaskItems = [
    ...caseTasks.map((t) => ({
      kind: "case", key: `ct-${t.case_id}-${t.task_id}`,
      status: t.status, is_delayed: t.is_delayed, days_until: t.days_until, deadline: t.deadline, delay_days: t.delay_days,
      title: t.case_title,
      subtitle: `${TYPE_LABEL[t.type] || t.type}${t.source_project_name ? ` · ${t.source_project_name}` : t.customer_name ? ` · ${t.customer_name}` : ""}`,
      task_name: t.task_name,
      is_first: t.is_first, prev_done: t.prev_done, prev_step_name: t.prev_step_name, prev_step_status: t.prev_step_status,
      first_label: "Sagens første opgave",
      responsible_label: "Sagsansvarlig", responsible_name: t.responsible_name,
      source: t.source_project_name ? `Projekt · ${t.source_project_name}` : "Ny sag",
      nav_to: `/sag/${t.case_id}`, button_label: "Åbn sag",
      can_complete: t.can_complete, case_id: t.case_id, task_id: t.task_id,
      testid: `case-${t.case_id}`,
    })),
    ...(priorities || []).map((t) => ({
      kind: "step", key: `ft-${t.step_id}`,
      status: t.status, is_delayed: t.is_delayed, days_until: t.days_until, deadline: t.deadline, delay_days: t.delay_days,
      title: t.project_name,
      subtitle: t.customer_name || "—",
      task_name: t.step_name,
      is_first: t.is_first, prev_done: t.prev_done, prev_step_name: t.prev_step_name, prev_step_status: t.prev_step_status,
      first_label: "Projektets første trin",
      responsible_label: "Projektansvarlig", responsible_name: t.project_manager_name,
      nav_to: `/projekt/${t.project_id}`, button_label: "Åbn projekt",
      can_complete: t.can_complete, step_id: t.step_id,
      testid: `step-${t.step_id}`,
    })),
    ...(myFindings || []).map((f) => {
      const dl = f.due_date || null;
      let days_until = null, delay_days = null;
      if (dl) { const diff = Math.round((new Date(dl) - new Date(todayISO)) / 86400000); days_until = diff; if (f.overdue) delay_days = Math.abs(diff); }
      return {
        kind: "finding", key: `fnd-${f.id}`,
        status: f.status, is_delayed: f.overdue, days_until, deadline: dl, delay_days,
        title: f.area_name || "Auditfund",
        subtitle: `${f.category} · ${f.audit_no} · ${f.finding_no}`,
        task_name: f.description || f.category,
        responsible_label: "Ansvarlig", responsible_name: f.responsible_name,
        nav_to: `/kvalitet/audit/fund/${f.id}`, button_label: "Åbn fund",
        can_complete: false, testid: `finding-${f.id}`, milestones: f.milestones || [],
      };
    }),
    ...(myLeActions || []).map((a) => {
      const dl = a.deadline || null;
      let days_until = null, delay_days = null;
      if (dl) { const diff = Math.round((new Date(dl) - new Date(todayISO)) / 86400000); days_until = diff; if (a.overdue) delay_days = Math.abs(diff); }
      return {
        kind: "le_action", key: `le-${a.id}`,
        status: a.status, is_delayed: a.overdue, days_until, deadline: dl, delay_days,
        title: a.origin_agenda_title || "Ledelsens evaluering",
        subtitle: "Ledelsens evaluering · Handlingsplan",
        task_name: a.description,
        responsible_label: "Ansvarlig", responsible_name: a.responsible_name,
        nav_to: `/handling/${a.id}`, button_label: "Åbn handling",
        can_complete: false, testid: `le-action-${a.id}`, milestones: a.milestones || [],
      };
    }),
    ...(myGoals || []).map((g) => {
      const dl = g.deadline || null;
      let days_until = null, delay_days = null;
      if (dl) { const diff = Math.round((new Date(dl) - new Date(todayISO)) / 86400000); days_until = diff; if (g.overdue) delay_days = Math.abs(diff); }
      return {
        kind: "goal", key: `goal-${g.id}`,
        status: g.status, is_delayed: g.overdue, days_until, deadline: dl, delay_days,
        title: g.title,
        subtitle: g.category ? `Kvalitets- & miljømål · ${g.category}` : "Kvalitets- & miljømål",
        task_name: g.goal_no || "Mål",
        responsible_label: "Ansvarlig", responsible_name: g.responsible_name,
        nav_to: `/maal/${g.id}`, button_label: "Åbn mål",
        can_complete: false, testid: `goal-${g.id}`, milestones: [],
      };
    }),
  ].sort((a, b) => {
    if (!a.deadline && !b.deadline) return 0;
    if (!a.deadline) return 1;
    if (!b.deadline) return -1;
    return a.deadline.localeCompare(b.deadline);
  });
  const shownProjects = (my_projects || []).slice(0, 4);

  return (
    <div className="max-w-[1240px] mx-auto space-y-14 py-2 rise-in" data-testid="home-dashboard">
      {bk?.reminder && <div className="rounded-2xl border border-[#E9C9C3] bg-[#FBF1EF] px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="backup-reminder"><p className="text-sm font-medium text-[#A83232]">Seneste backup: {bk.days_since == null ? "aldrig" : `${bk.days_since} dage siden`} — hent backup</p><button type="button" onClick={() => nav("/admin/backup")} data-testid="backup-reminder-link" className="h-9 px-4 rounded-xl bg-[#A83232] text-white text-sm font-medium hover:bg-[#8f2a2a]">Hent backup</button></div>}
      {/* 1. Personlig velkomst */}
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-[#1A1919]">{greeting()}, {name}</h1>
          <p className="text-stone-500 mt-2 text-[15px]" data-testid="home-welcome">
            {focus.action_required > 0
              ? <>Du har <span className="text-[#1A1919] font-medium">{focus.action_required} opgaver</span>, der kræver handling.</>
              : "Du har ingen opgaver, der kræver handling lige nu."}
          </p>
          <p className="text-sm text-stone-400 mt-1">{cap(format(new Date(), "EEEE 'den' d. MMMM yyyy", { locale: da }))}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0" data-testid="home-user">
          {user?.avatar_url
            ? <img src={`${BACKEND}${user.avatar_url}`} alt={user?.name || "Bruger"} className="h-32 w-32 rounded-full object-cover border border-[#EBE8E3]" data-testid="home-avatar" />
            : <div className="h-32 w-32 rounded-full bg-[#F1DEDA] text-[#A83232] flex items-center justify-center font-semibold text-4xl border border-[#E9C9C3]" data-testid="home-avatar-fallback">{initials}</div>}
        </div>
      </header>

      {/* Nyt fra kunder — beskeder + statusopdateringer i én kolonne. Vises for Salg, og for ALLE med åbne beskeder/statusopdateringer på egne projekter (projektansvarlig i anden afdeling). */}
      {(isSalgView || (alerts?.total || 0) + (statusAlerts?.total || 0) > 0) && (() => {
        const acts = [
          ...((alerts?.items) || []).map((a) => ({ ...a, kind: "msg" })),
          ...((statusAlerts?.items) || []).map((a) => ({ ...a, kind: "status" })),
        ];
        const total = (alerts?.total || 0) + (statusAlerts?.total || 0);
        return (
          <section data-testid="customer-activity">
            {total > 0 ? (
              <div className="rounded-2xl border border-[#E9C9C3] bg-[#F1DEDA] p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-9 w-9 rounded-xl bg-white flex items-center justify-center shrink-0"><Bell className="h-4 w-4 text-[#A83232]" /></div>
                  <div>
                    <p className="font-semibold text-[#1A1919]">Nyt fra kunder</p>
                    <p className="text-sm text-stone-600">
                      {alerts?.total ? `${alerts.total} ${alerts.total === 1 ? "ny besked" : "nye beskeder"}` : ""}
                      {alerts?.total && statusAlerts?.total ? " · " : ""}
                      {statusAlerts?.total ? `${statusAlerts.total} ${statusAlerts.total === 1 ? "statusopdatering" : "statusopdateringer"}` : ""}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {acts.map((a) => {
                    const isMsg = a.kind === "msg";
                    const accent = isMsg ? "#A83232" : "#8A6E17";
                    const Icon = isMsg ? MessageSquare : CircleDot;
                    return (
                      <button key={`${a.kind}-${a.project_id}`} onClick={() => nav(`/projekt/${a.project_id}?tab=${isMsg ? "comments" : "steps"}`)} data-testid={`activity-${a.kind}-${a.project_id}`}
                        className="w-full text-left flex items-center gap-3 rounded-xl bg-white border border-[#EBE8E3] px-4 py-3 hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)] transition-all">
                        <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: isMsg ? "#F1DEDA" : "#F9F1D9" }}><Icon className="h-4 w-4" style={{ color: accent }} /></div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-[#1A1919] truncate">{a.project_name}</p>
                          <p className="text-xs text-stone-500 truncate">{isMsg ? `Ny besked${a.customer_name ? " · " + a.customer_name : ""}` : (a.latest_step ? <>Status: {a.latest_step} → <span className="font-medium">{a.latest_status}</span></> : "Statusopdatering")}</p>
                        </div>
                        <span className="text-xs font-semibold text-white rounded-full px-2 py-0.5 shrink-0" style={{ background: accent }}>{a.unread}</span>
                        <ChevronRight className="h-4 w-4 shrink-0" style={{ color: accent }} />
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-[#EBE8E3] bg-white p-4 flex items-center gap-3" data-testid="customer-activity-empty">
                <div className="h-9 w-9 rounded-xl bg-[#F3F1EC] flex items-center justify-center shrink-0"><Bell className="h-4 w-4 text-stone-400" /></div>
                <div>
                  <p className="font-medium text-[#1A1919]">Nyt fra kunder</p>
                  <p className="text-sm text-stone-500">Ingen nye beskeder eller statusopdateringer lige nu.</p>
                </div>
              </div>
            )}
          </section>
        );
      })()}

      {/* Nyt fra interne sager — statusændringer + nære deadlines på projekt-sager */}
      {(() => {
        const statusItems = (caseAlerts?.status?.items) || [];
        const deadlineItems = (caseAlerts?.deadline?.items) || [];
        const createdItems = (caseAlerts?.created?.items) || [];
        const total = (caseAlerts?.status?.total || 0) + (caseAlerts?.deadline?.total || 0) + (caseAlerts?.created?.total || 0);
        const TYPE = { kvalitet: "Kvalitet", indkoeb: "Indkøb" };
        return (
          <section data-testid="internal-case-activity" className="mt-4">
            {total > 0 ? (
              <div className="rounded-2xl border border-[#ECE0B6] bg-[#F9F1D9] p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-9 w-9 rounded-xl bg-white flex items-center justify-center shrink-0"><Building2 className="h-4 w-4 text-[#8A6E17]" /></div>
                  <div>
                    <p className="font-semibold text-[#1A1919]">Nyt fra interne sager</p>
                    <p className="text-sm text-stone-600">
                      {[caseAlerts?.created?.total ? `${caseAlerts.created.total} ${caseAlerts.created.total === 1 ? "ny sag via salg" : "nye sager via salg"}` : "", caseAlerts?.status?.total ? `${caseAlerts.status.total} ${caseAlerts.status.total === 1 ? "statusændring" : "statusændringer"}` : "", caseAlerts?.deadline?.total ? `${caseAlerts.deadline.total} ${caseAlerts.deadline.total === 1 ? "deadline" : "deadlines"} inden for 3 dage` : ""].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {createdItems.map((a) => (
                    <button key={`cn-${a.case_id}`} onClick={() => nav(`/sag/${a.case_id}`)} data-testid={`case-activity-created-${a.case_id}`}
                      className="w-full text-left flex items-center gap-3 rounded-xl bg-white border border-[#ECE0B6] px-4 py-3 hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)] transition-all">
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-[#F9F1D9]"><Building2 className="h-4 w-4 text-[#8A6E17]" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#1A1919] truncate">{a.title}</p>
                        <p className="text-xs text-stone-500 truncate">Der er oprettet en ny sag via salg{a.days_until != null ? ` · deadline ${a.days_until <= 0 ? "i dag" : a.days_until === 1 ? "i morgen" : `om ${a.days_until} dage`}` : ""} · Ansvarlig: {a.responsible_name}</p>
                      </div>
                      <span className="text-[10px] font-semibold text-[#8A6E17] bg-[#F9F1D9] rounded-full px-2 py-0.5 shrink-0">NY</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-[#8A6E17]" />
                    </button>
                  ))}
                  {statusItems.map((a) => (
                    <button key={`cs-${a.case_id}`} onClick={() => nav(`/sag/${a.case_id}`)} data-testid={`case-activity-status-${a.case_id}`}
                      className="w-full text-left flex items-center gap-3 rounded-xl bg-white border border-[#EBE8E3] px-4 py-3 hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)] transition-all">
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-[#F9F1D9]"><CircleDot className="h-4 w-4 text-[#8A6E17]" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#1A1919] truncate">{a.title}</p>
                        <p className="text-xs text-stone-500 truncate">{TYPE[a.type] || a.type}{a.source_project_name ? " · " + a.source_project_name : ""} · Status: <span className="font-medium">{a.latest_status}</span> · Ansvarlig: {a.responsible_name}</p>
                      </div>
                      <span className="text-xs font-semibold text-white rounded-full px-2 py-0.5 shrink-0 bg-[#8A6E17]">{a.unread}</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-[#8A6E17]" />
                    </button>
                  ))}
                  {deadlineItems.map((a) => (
                    <button key={`cd-${a.case_id}`} onClick={() => nav(`/sag/${a.case_id}`)} data-testid={`case-activity-deadline-${a.case_id}`}
                      className="w-full text-left flex items-center gap-3 rounded-xl bg-white border border-[#E9C9C3] px-4 py-3 hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)] transition-all">
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-[#F1DEDA]"><CalendarClock className="h-4 w-4 text-[#A83232]" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#1A1919] truncate">{a.title}</p>
                        <p className="text-xs text-stone-500 truncate">{TYPE[a.type] || a.type}{a.source_project_name ? " · " + a.source_project_name : ""} · Deadline {a.days_until <= 0 ? "i dag" : a.days_until === 1 ? "i morgen" : `om ${a.days_until} dage`} · Ansvarlig: {a.responsible_name} · {a.status}</p>
                      </div>
                      <span className="text-xs font-semibold text-white rounded-full px-2 py-0.5 shrink-0 bg-[#A83232]">!</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-[#A83232]" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-[#EBE8E3] bg-white p-4 flex items-center gap-3" data-testid="internal-case-activity-empty">
                <div className="h-9 w-9 rounded-xl bg-[#F3F1EC] flex items-center justify-center shrink-0"><Building2 className="h-4 w-4 text-stone-400" /></div>
                <div>
                  <p className="font-medium text-[#1A1919]">Nyt fra interne sager</p>
                  <p className="text-sm text-stone-500">Ingen statusændringer eller deadlines inden for 3 dage lige nu.</p>
                </div>
              </div>
            )}
          </section>
        );
      })()}

      {/* Nyt fra Produktion — nye dokumenter (kun mathias@bsm.dk) */}
      {(user?.email || "").toLowerCase() === "mathias@bsm.dk" && (() => {
        const items = docAlerts?.items || [];
        const total = docAlerts?.total || 0;
        const LBL = { svejsning: "Svejseafdeling", montage: "Montageafdeling", slibning: "Slibeafdeling" };
        const IC = { svejsning: Flame, montage: Wrench, slibning: Disc3 };
        const openDoc = async (a) => { try { await api.post(`/me/document-alerts/${a.id}/seen`); } catch (_) { /* noop */ } nav(`/dokument/${a.id}`); };
        const dismiss = async (a, e) => { e.stopPropagation(); try { await api.post(`/me/document-alerts/${a.id}/seen`); } catch (_) { /* noop */ } setDocAlerts((d) => ({ total: Math.max(0, (d?.total || 1) - 1), items: (d?.items || []).filter((x) => x.id !== a.id) })); };
        const clearAll = async () => { try { await api.post("/me/document-alerts/seen-all"); } catch (_) { /* noop */ } setDocAlerts({ total: 0, items: [] }); };
        return (
          <section data-testid="production-doc-activity" className="mt-4">
            {total > 0 ? (
              <div className="rounded-2xl border border-[#E6DDD6] bg-[#F4EFEC] p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-9 w-9 rounded-xl bg-white flex items-center justify-center shrink-0"><Factory className="h-4 w-4 text-[#806B61]" /></div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1A1919]">Nyt fra Produktion</p>
                    <p className="text-sm text-stone-600">{total} {total === 1 ? "nyt dokument" : "nye dokumenter"} oprettet</p>
                  </div>
                  <button onClick={clearAll} data-testid="prod-doc-clear-all" className="ml-auto text-xs font-medium text-stone-500 hover:text-[#806B61] bg-white border border-[#E6DDD6] rounded-lg px-2.5 py-1.5">Ryd alle</button>
                </div>
                <div className="space-y-2">
                  {items.map((a) => {
                    const Icon = IC[a.type] || FileText;
                    return (
                      <div key={a.id} onClick={() => openDoc(a)} data-testid={`prod-doc-alert-${a.id}`} role="button" tabIndex={0}
                        className="w-full text-left flex items-center gap-3 rounded-xl bg-white border border-[#E6DDD6] px-4 py-3 hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)] transition-all cursor-pointer">
                        <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-[#F4EFEC]"><Icon className="h-4 w-4 text-[#806B61]" /></div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-[#1A1919] truncate">{a.title || "Uden titel"}</p>
                          <p className="text-xs text-stone-500 truncate">{LBL[a.type] || a.type}{a.item_no ? ` · Varenr. ${a.item_no}` : ""}{a.created_by_name ? ` · Oprettet af ${a.created_by_name}` : ""}</p>
                        </div>
                        <span className="text-[10px] font-semibold text-[#806B61] bg-[#F4EFEC] rounded-full px-2 py-0.5 shrink-0">NYT</span>
                        <button onClick={(e) => dismiss(a, e)} data-testid={`prod-doc-dismiss-${a.id}`} className="p-1 text-stone-300 hover:text-[#806B61] shrink-0"><X className="h-4 w-4" /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-[#EBE8E3] bg-white p-4 flex items-center gap-3" data-testid="production-doc-activity-empty">
                <div className="h-9 w-9 rounded-xl bg-[#F3F1EC] flex items-center justify-center shrink-0"><Factory className="h-4 w-4 text-stone-400" /></div>
                <div>
                  <p className="font-medium text-[#1A1919]">Nyt fra Produktion</p>
                  <p className="text-sm text-stone-500">Ingen nye produktionsdokumenter lige nu.</p>
                </div>
              </div>
            )}
          </section>
        );
      })()}

      {/* Mine opgaver — fælles kort for sager og projekttrin, sorteret efter deadline */}
      <section data-testid="focus-section">
        <h2 className="label-caps mb-5">Mine aktive opgaver</h2>
        {myTaskItems.length === 0 ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-10 text-center" data-testid="focus-empty">
            <CheckCircle2 className="h-9 w-9 mx-auto text-[#7E856C]" strokeWidth={1.5} />
            <p className="mt-3 text-[#1A1919] font-medium">Alt er under kontrol</p>
            <p className="text-sm text-stone-500 mt-1">Du har ingen opgaver, hvor du er sat som ansvarlig lige nu.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {(expandedTasks ? myTaskItems : myTaskItems.slice(0, 6)).map((item) => (
                <TaskCard key={item.key} item={item} busy={busy} onDone={markDone} nav={nav} />
              ))}
            </div>
            {myTaskItems.length > 6 && (
              <button onClick={() => setExpandedTasks((v) => !v)} data-testid="expand-tasks" className="mt-5 text-sm text-[#A83232] font-medium inline-flex items-center gap-1 hover:gap-1.5 transition-all">
                {expandedTasks ? "Vis færre" : `Vis mere (${myTaskItems.length})`} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}
      </section>

      {isSalgView && (<>
      {/* 4. Mine projekter */}
      <section data-testid="my-projects-section">
        <div className="flex items-center justify-between mb-5">
          <h2 className="label-caps">Projekter, hvor jeg er projektansvarlig</h2>
          {my_projects.length > 4 && (
            <button onClick={() => nav("/projekter?projektansvarlig=me")} data-testid="see-all-projects" className="text-sm text-[#A83232] font-medium inline-flex items-center gap-1 hover:gap-1.5 transition-all">Se alle projekter <ArrowRight className="h-3.5 w-3.5" /></button>
          )}
        </div>
        {shownProjects.length === 0 ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-10 text-center text-sm text-stone-400" data-testid="projects-empty">Du er ikke tilknyttet nogen projekter endnu.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {shownProjects.map((p) => (
              <button key={p.id} onClick={() => nav(`/projekt/${p.id}`)} data-testid={`project-card-${p.id}`} className={`rounded-2xl border p-6 text-left transition-colors bg-white ${p.delayed_count > 0 ? "border-[#A83232] hover:border-[#A83232]" : "border-[#EBE8E3] hover:border-stone-300"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1A1919] truncate">{p.name}</p>
                    <p className="text-sm text-stone-500 truncate mt-0.5">{p.customer_name || "—"}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs text-stone-500 shrink-0 mt-1"><span className={`h-1.5 w-1.5 rounded-full ${PROJ_DOT[p.status]}`} /> {PROJ_STATUS[p.status]}</span>
                </div>
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    {(() => { const track = p.status === "ikke_igangsat" ? { label: "Ikke igangsat", cls: "text-stone-500", dot: "bg-stone-300" } : p.status === "afsluttet" ? { label: "Afsluttet", cls: "text-[#7E856C]", dot: "bg-[#7E856C]" } : p.delayed_count > 0 ? { label: `${p.delayed_count} ${p.delayed_count === 1 ? "trin" : "trin"} forsinket`, cls: "text-[#A83232]", dot: "bg-[#A83232]" } : { label: "On track", cls: "text-[#7E856C]", dot: "bg-[#7E856C]" }; return (
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${track.cls}`} data-testid={`project-track-${p.id}`}><span className={`h-1.5 w-1.5 rounded-full ${track.dot}`} /> {track.label}</span>
                    ); })()}
                    <span className="text-xs font-medium text-stone-500">{p.progress}%</span>
                  </div>
                  <div className="h-1 rounded-full bg-[#ECEAE5] overflow-hidden"><div className="h-full rounded-full bg-[#A83232]" style={{ width: `${p.progress}%` }} /></div>
                </div>
                <div className="mt-4 pt-4 border-t border-[#F3F1EC] space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-stone-400">Aktuelt trin</span>
                    <span className="text-stone-700 font-medium truncate max-w-[60%] text-right">{p.current_step_name}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-stone-400">Frist for trin</span>
                    <span className="text-stone-500 text-right" data-testid={`project-step-deadline-${p.id}`}>{p.current_step_deadline ? daDate(p.current_step_deadline) : "Ikke fastlagt"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-stone-400">Trinansvarlig</span>
                    <span className="text-stone-500 truncate max-w-[60%] text-right">{p.current_step_responsible_name || "—"}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
      </>)}

      {/* Mine oprettede sager (kvalitet/indkøb) — KUN aktive. Løste/lukkede ses under "Opret ny sag". */}
      <section data-testid="created-cases-section">
        <h2 className="label-caps mb-5">Mine aktive interne cases</h2>
        {createdCases.length === 0 ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-10 text-center text-sm text-stone-400" data-testid="created-cases-empty">Du har ikke oprettet nogen aktive sager.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {(expandedCases ? createdCases : createdCases.slice(0, 6)).map((c) => <CreatedCaseCard key={c.case_id} c={c} nav={nav} />)}
            </div>
            {createdCases.length > 6 && (
              <button onClick={() => setExpandedCases((v) => !v)} data-testid="expand-created-cases" className="mt-5 text-sm text-[#A83232] font-medium inline-flex items-center gap-1 hover:gap-1.5 transition-all">
                {expandedCases ? "Vis færre" : `Vis alle (${createdCases.length})`} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}
      </section>

      {company && company.load_forecast && company.load_forecast.summary && company.load_forecast.summary.total > 0 && (() => {
        const fc = company.load_forecast;
        const overEmp = new Set(fc.warnings.filter((w) => w.status === "Overbelastet").map((w) => w.user_id)).size;
        const highEmp = new Set(fc.warnings.filter((w) => w.status === "Høj").map((w) => w.user_id)).size;
        return (
          <section data-testid="load-forecast-card">
            <h2 className="label-caps mb-5">Kommende belastning</h2>
            <button onClick={() => nav("/admin/total-planlaegning")} data-testid="load-forecast-open" className="w-full text-left rounded-2xl border border-[#E9C9C3] bg-[#F1DEDA]/40 p-6 hover:bg-[#F1DEDA]/70 [transition-property:background-color] duration-200">
              <div className="flex items-start gap-4">
                <span className="h-10 w-10 rounded-xl bg-[#A83232]/10 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5 text-[#A83232]" strokeWidth={2} /></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#1A1919] font-medium">{fc.summary.total} kommende belastningsproblem{fc.summary.total === 1 ? "" : "er"} · første i uge {fc.summary.earliest_week}</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-stone-600">
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#A83232]" /> {overEmp} medarbejder{overEmp === 1 ? "" : "e"} bliver overbelastede</span>
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#A08979]" /> {highEmp} medarbejder{highEmp === 1 ? "" : "e"} med høj belastning</span>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#A83232] shrink-0 mt-1" />
              </div>
            </button>
          </section>
        );
      })()}

      {/* Belastningsoversigt (kun admin) — placeret under Mine oprettede sager */}
      {company && (
        <section data-testid="load-overview">
          <h2 className="label-caps mb-5">Belastningsoversigt · alle medarbejdere</h2>
          {company.top_loaded.length === 0 ? (
            <div className="rounded-2xl border border-[#EBE8E3] bg-white p-10 text-center text-sm text-stone-400">Ingen aktive opgaver fordelt endnu.</div>
          ) : (
            <div className="rounded-2xl border border-[#EBE8E3] bg-white divide-y divide-[#F3F1EC]">
              {company.top_loaded.map((u) => {
                const meta = LOAD_META[u.load_label] || LOAD_META["Normal"];
                const open = openLoad === u.user_id;
                return (
                  <div key={u.user_id}>
                    <button onClick={() => setOpenLoad(open ? null : u.user_id)} className="w-full text-left flex items-center gap-4 px-5 py-4 hover:bg-[#FBFAF8] transition-colors" data-testid={`load-row-${u.user_id}`}>
                      <span className="text-sm font-medium text-[#1A1919] w-48 shrink-0 truncate">{u.name}</span>
                      <div className="hidden sm:block flex-1 h-1.5 rounded-full bg-[#ECEAE5] overflow-hidden">
                        <div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${Math.min((u.week_load || 0) / 7 * 100, 100)}%` }} />
                      </div>
                      <span className="text-sm text-stone-500 shrink-0 ml-auto sm:ml-0 tabular-nums">{u.week_load || 0} pr. uge · {u.active_tasks} i alt</span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-lg border px-2.5 py-1 shrink-0 ${meta.chip}`}><span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {u.load_label}</span>
                      <ChevronDown className={`h-4 w-4 text-stone-400 shrink-0 [transition-property:transform] duration-200 ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <div className="bg-[#FBFAF8] px-5 py-3 space-y-2" data-testid={`load-tasks-${u.user_id}`}>
                        {(u.tasks || []).length === 0 ? (
                          <p className="text-sm text-stone-400 py-2">Ingen aktive opgaver.</p>
                        ) : u.tasks.map((t) => {
                          const sm = STATUS_META[t.status] || STATUS_META["Ikke startet"];
                          return (
                            <button key={t.step_id} onClick={() => nav(t.kind === "sag" ? `/sag/${t.project_id}` : `/projekt/${t.project_id}`)} data-testid={`load-task-${t.step_id}`} className="w-full text-left rounded-xl border border-[#EBE8E3] bg-white hover:border-stone-300 transition-colors px-4 py-2.5 flex items-center gap-3">
                              <div className="min-w-0 flex-1"><p className="font-medium text-[#1A1919] truncate text-sm">{t.step_name}</p><p className="text-xs text-stone-500 truncate mt-0.5">{t.project_name}{t.customer_name ? ` · ${t.customer_name}` : ""}</p></div>
                              <div className="text-right shrink-0"><p className={`text-xs font-medium ${t.is_delayed ? "text-[#A83232]" : "text-stone-500"}`}>{t.deadline ? daDate(t.deadline) : "Ingen frist"}</p><span className={`inline-flex items-center gap-1 text-[11px] mt-0.5 ${sm.text}`}><span className={`h-1.5 w-1.5 rounded-full ${sm.dot}`} /> {t.status}</span></div>
                              <ChevronRight className="h-4 w-4 text-stone-300 shrink-0" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-stone-400 mt-3">Ingen belastning = 0 · Normal 1–4 · Høj belastning 5–8 · Overbelastet 9+ opgaver i indeværende uge. Én opgave tælles én gang pr. uge; opgaver over flere uger tælles i hver berørt uge.</p>
        </section>
      )}
    </div>
  );
}

const DOC_STATUS = {
  Kladde: { bg: "#F3F1EC", tx: "#78716C", dot: "#A8A29E" },
  "I gang": { bg: "#F4EFEC", tx: "#806B61", dot: "#A08979" },
  "Færdig": { bg: "#E7EADF", tx: "#7E856C", dot: "#7E856C" },
};
const DEPTS = [
  { type: "svejsning", label: "Svejseafdeling", desc: "Svejsedokumentation", icon: Flame, to: "/produktion/svejsning" },
  { type: "montage", label: "Montageafdeling", desc: "Montagedokumentation", icon: Wrench, to: "/produktion/montage" },
  { type: "slibning", label: "Slibeafdeling", desc: "Slibningsdokumentation", icon: Disc3, to: "/produktion/slibning" },
];

function ProductionHome({ user, nav }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => { api.get("/quality-reports?module=dokumenter").then((r) => setDocs(r.data || [])).catch(() => setDocs([])); }, []);
  const name = (user?.name || "").split(" ")[0] || user?.name;
  const initials = (user?.name || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const count = (t) => (docs || []).filter((r) => r.type === t).length;
  const recent = [...(docs || [])].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")).slice(0, 6);

  return (
    <div className="max-w-[1240px] mx-auto space-y-12 py-2 rise-in" data-testid="production-home">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-[#1A1919]">{greeting()}, {name}</h1>
          <p className="text-stone-500 mt-2 text-[15px]">Vælg en afdeling for at se og oprette dokumenter.</p>
          <p className="text-sm text-stone-400 mt-1">{cap(format(new Date(), "EEEE 'den' d. MMMM yyyy", { locale: da }))}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {user?.avatar_url
            ? <img src={`${BACKEND}${user.avatar_url}`} alt={user?.name || "Bruger"} className="h-24 w-24 rounded-full object-cover border border-[#EBE8E3]" data-testid="prod-avatar" />
            : <div className="h-24 w-24 rounded-full bg-[#F1DEDA] text-[#A83232] flex items-center justify-center font-semibold text-3xl border border-[#E9C9C3]" data-testid="prod-avatar-fallback">{initials}</div>}
        </div>
      </header>

      <section data-testid="prod-departments">
        <h2 className="label-caps mb-5">Afdelinger</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {DEPTS.map((dp) => (
            <button key={dp.type} onClick={() => nav(dp.to)} data-testid={`prod-dept-${dp.type}`}
              className="rounded-2xl border border-[#EBE8E3] bg-white p-6 text-left hover:border-[#A83232]/40 hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)] [transition-property:border-color,box-shadow] duration-200 flex flex-col">
              <span className="h-11 w-11 rounded-xl bg-[#F1DEDA] flex items-center justify-center"><dp.icon className="h-5 w-5 text-[#A83232]" strokeWidth={2} /></span>
              <p className="font-semibold text-[#1A1919] mt-4">{dp.label}</p>
              <p className="text-sm text-stone-500 mt-0.5">{dp.desc}</p>
              <div className="mt-4 pt-4 border-t border-[#F3F1EC] flex items-center justify-between">
                <span className="text-sm text-stone-500">{docs === null ? "…" : `${count(dp.type)} ${count(dp.type) === 1 ? "dokument" : "dokumenter"}`}</span>
                <span className="text-sm text-[#A83232] font-medium inline-flex items-center gap-1">Åbn <ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section data-testid="prod-recent">
        <h2 className="label-caps mb-5">Seneste dokumenter</h2>
        {docs === null ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-10 text-center text-sm text-stone-400">Indlæser…</div>
        ) : recent.length === 0 ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-10 text-center" data-testid="prod-recent-empty">
            <div className="h-12 w-12 rounded-2xl bg-[#F3F1EC] flex items-center justify-center mx-auto mb-3"><FileText className="h-5 w-5 text-stone-400" /></div>
            <p className="text-stone-600 font-medium">Ingen dokumenter endnu</p>
            <p className="text-sm text-stone-400 mt-1">Vælg en afdeling ovenfor for at oprette dit første dokument.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white divide-y divide-[#F3F1EC]" data-testid="prod-recent-list">
            {recent.map((r) => {
              const dp = DEPTS.find((x) => x.type === r.type);
              const Icon = dp?.icon || FileText;
              const st = DOC_STATUS[r.status] || DOC_STATUS.Kladde;
              return (
                <button key={r.id} onClick={() => nav(`/dokument/${r.id}`)} data-testid={`prod-recent-${r.id}`}
                  className="w-full text-left flex items-center gap-4 px-5 py-4 hover:bg-[#FBFAF8] transition-colors">
                  <span className="h-9 w-9 rounded-lg bg-[#F1DEDA] flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-[#A83232]" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[#1A1919] truncate">{r.title || "Uden titel"}</p>
                    <p className="text-xs text-stone-500 truncate mt-0.5">{dp?.label || r.type}{r.item_no ? ` · Varenr. ${r.item_no}` : ""}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0" style={{ background: st.bg, color: st.tx }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: st.dot }} />{r.status}</span>
                  <ChevronRight className="h-4 w-4 text-stone-300 shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}